<template>
  <div class="tla-form-group">
    <label class="tla-form-group__label" v-if="!hideLabel">{{label}}</label>

    <div class="tla-timepicker__wrapper" :class="{ 'tla-timepicker__wrapper--active': isOpen }">
      <div
        class="tla-form-group__input tla-form-group__input--arrow"
        :class="{'tla-form-group__input--disabled' : isDisabled}"
        @click="openTimeSelector(!isOpen)"
        @focus="openTimeSelector(true)"
        @blur="openTimeSelector(false)">
        <span>{{ inputPlaceholder }}</span>
      </div>
      <div class="tla-timepicker" v-if="isOpen" @blur="openTimeSelector(false)">
        <div class="tla-timepicker__header">
          <h2 class="tla-timepicker__heading">{{ dateFormatted }}</h2>
          <h3 class="tla-timepicker__sub-heading">{{ selectedTimeSlot ? timeFormatted : 'Please choose a time' }}</h3>
        </div>

        <ul class="tla-timepicker__list">
          <li class="tla-timepicker__item" v-for="time in timeSlots" :key="time.id">
            <button
              class="tla-timepicker__btn"
              :class="{ 'tla-timepicker__btn--selected': selectedTimeSlot && selectedTimeSlot.id === time.id }"
              @click="selectTime(time)"
            >
              {{ time12HourFormatted(time.start) }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Vue, Component, Prop, Watch
} from 'vue-property-decorator';
import TimeSlot from '../../models/date-picker/TimeSlot';
import SelectedTimeSlot from '../../models/date-picker/SelectedTimeSlot';
import DateUtils from '../../utils/DateUtils';

@Component
export default class TimePickerComponent extends Vue {
  @Prop()
  public label!: string;

  @Prop()
  public placeholder!: string;

  @Prop()
  public date!: Date;

  @Prop()
  public timeSlots!: TimeSlot[];

  @Prop({ default: false })
  public hideLabel!: boolean;

  @Prop({ default: false })
  public defaultTimepickerOpen!: boolean;

  public isOpen: boolean = false;

  public selectedTimeSlot: TimeSlot | null = null;

  public selectTime(time: TimeSlot) {
    this.selectedTimeSlot = time;
  }

  public get isDisabled(): boolean {
    return !this.timeSlots || this.timeSlots.length === 0;
  }

  public get dateFormatted(): string {
    return DateUtils.getDateFormatted(this.date, true);
  }

  public get timeFormatted(): string {
    if (!this.selectedTimeSlot) {
      return '';
    }

    const startDate = this.selectedTimeSlot.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const endDate = this.selectedTimeSlot.end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    return `${startDate} - ${endDate}`;
  }

  public time12HourFormatted(date: Date): string {
    return DateUtils.getTime12HourFormatted(date);
  }

  public get inputPlaceholder(): string {
    if (!this.selectedTimeSlot) {
      return this.placeholder;
    }

    const dateFormatted = this.selectedTimeSlot.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const suffix = DateUtils.getTimeSuffix(this.selectedTimeSlot.start.getHours());

    return `${dateFormatted}${suffix}`;
  }

  public openTimeSelector(newState: boolean) {
    if (newState && !this.isDisabled) {
      this.isOpen = true;
      return;
    }

    this.isOpen = false;
  }

  @Watch('date')
  public onDateChanged() {
    this.selectedTimeSlot = null;
  }

  @Watch('selectedTimeSlot')
  public onTimeSlotChanged(value: TimeSlot) {
    if (value === null) {
      this.$emit('input', null);
    } else {
      const newSlot = new SelectedTimeSlot(
        value.id,
        value.start,
        value.end,
        this.dateFormatted,
        this.time12HourFormatted(value.start),
        this.time12HourFormatted(value.end),
        this.timeFormatted
      );

      this.$emit('input', newSlot);
    }

    this.isOpen = false;
  }

  @Watch('defaultTimepickerOpen')
  public onDefaultTimepickerOpenChanged(value: boolean) {
    this.openTimeSelector(value);
  }
}
</script>
